import { Component, OnDestroy, OnInit } from '@angular/core';
import { UserService } from 'src/app/core/api/user/user.service';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom, lastValueFrom, Subscription, take } from 'rxjs';
import { RegisterService } from 'src/app/core/services/register/register.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { DialogModalComponent } from 'src/app/shared/components/molecules/dialog-modal/dialog-modal.component';
import { BusinessDataFormObject } from 'src/app/shared/models/business-data/business-data-form-object/business-data-form-object';
import { BusinessEquipmentFormObject } from 'src/app/shared/models/business-equipment/business-equipment-form-object/business-equipment-form-object';
import { MatDialogResponse } from 'src/app/shared/models/constants/mat-dialog-response';
import { BusinessRepresentativeInteraction } from 'src/app/shared/models/constants/business-representative-interaction';
import {
  AbstractControl,
  FormArray,
  FormGroup,
  FormControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  emailPattern,
  errorObjectBuilder,
  notInValidator,
  serialValidator,
} from 'src/app/shared/validators/registration-validators';
import { Representative } from 'src/app/shared/models/dtos/register-form/representative/representative';
import { RepresentativeItemLabels } from 'src/app/shared/models/representative-item-labels/representative-item-labels';
import { InviteRepresentativeRequestDTO } from 'src/app/shared/models/dtos/requests/invite-representative-request-dto/invite-representative-request-dto';
import { MessageRejectedException } from 'src/app/shared/errors/cognito/cognito-errors';
import { InviteRepresentativeResponseDTO } from 'src/app/shared/models/dtos/responses/invite-representative-response-dto/invite-representative-response-dto';
import {
  GeneralizedRepresentative,
  isRepresentative,
} from 'src/app/shared/models/generalized-representative/generalized-representative';
import { DataUpdateFeedback } from 'src/app/shared/models/data-update-feedback/data-update-feedback';
import { RepresentativeLimitReachedException } from 'src/app/shared/errors/user-management-service/user-management-service-errors';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { ProductService } from 'src/app/core/api/product/product.service';
import { AddRemoveEquipmentRequestDTO } from 'src/app/shared/models/dtos/requests/add-remove-equipments-request-dto/add-remove-equipments-request-dto';
import { NavigationService } from 'src/app/core/services/navigation/navigation.service';
import { TBusinessEquipmentFormPhase } from 'src/app/shared/components/molecules/business-equipment-form/business-equipment-form.component';
import { EphemeralFeedback } from 'src/app/shared/models/ephemeral-feedback/ephemeral-feedback';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { fromTRegisterEquipmentFormGroup } from 'src/app/shared/models/dtos/requests/equipment-sap-registrarion-request-dto/equipment-sap-registration-request-dto';
import { TopHeaderService } from 'src/app/core/services/top-header/top-header.service';
import { RepresentativeRole } from 'src/app/shared/models/dtos/register-form/representative/representative-role';

@Component({
  selector: 'app-business-settings',
  templateUrl: './business-settings.page.html',
  styleUrls: ['./business-settings.page.scss'],
})
/**
 * Business settings page that instantiates a template with information
 */
export class BusinessSettingsPage implements OnInit, OnDestroy {
  businessData!: BusinessDataFormObject;
  originalBusinessData!: any;
  equipmentData!: BusinessEquipmentFormObject;
  originalEquipmentData!: { equipments: Array<any> };
  manageRepresentatives!: UntypedFormGroup;
  businessId?: number;
  private promoteDialogData: any;
  private deleteRepresentativeDialogData: any;
  private deleteInviteDialogData: any;
  private resendInviteDialogData: any;
  private subsink = new Subscription();
  representativeItemLabels = new RepresentativeItemLabels(
    'promote',
    'delete',
    'resend-invite'
  );
  representativeList: Representative[] = [];
  inviteeList: InviteRepresentativeResponseDTO[] = [];
  inviteLoading = false;
  listLoading: boolean = false;
  inviteErrorFeedback = new DataUpdateFeedback();
  businessDataLoading = false;
  equipmentDataLoading = true;
  equipmentActionLoading = false;
  saveDataFeedback = new DataUpdateFeedback();
  saveEquipmentFeedback = new DataUpdateFeedback();
  equipmentIsChanged = false;
  isUpdateBusiness = true;
  errorMessages = {
    cantUpdateEmptyBusinessId: 'cant-update-empty-business-id',
    repIdMustBePresent: 'representative-id-must-be-present',
    invalidForm: 'invalid-form',
  };
  businessEquipmentFormPhase: TBusinessEquipmentFormPhase = 'LNR_REGISTER';
  readonly ephemeralFeedback = new EphemeralFeedback<
    'eligible' | 'registered'
  >();
  readonly labels = {
    couldNotCheckPrinterSerialData: 'unable-check-printer-serial-number',
    couldNotRegisterPrinter: 'unable-register-printer',
    noCommPrinterDatabase: 'could-not-communicate-with-printer-database',
    ok: 'ok',
    doYouWishToSaveChanges: 'do-you-wish-to-save-changes',
    cancel: 'cancel',
    saveChanges: 'save-changes',
    editBusinessEquipment: 'edit-business-equipment',
    equipmentsBeingADDED: 'equipments-being-added',
    equipmentsBeingREMOVED: 'equipments-being-removed',
    somethingWrong: 'something-wrong',
    title500Points: 'title-confirmation-modal-points',
    content500Points: 'confirmation-modal-points',
    validate: 'validate',
  };
  privacyLink = '';
  isAddingEquipment = false;
  page = 0;
  pageSize = 25;
  serverError = false;
  equipmentExistsError = false;
  newEquipmentFormGroup?: FormGroup = new FormGroup({
    serial: new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(10),
      Validators.maxLength(18),
      serialValidator(),
    ]),
    planModel: new UntypedFormControl('New Printer', [Validators.required]),
  });

  /**
   * @constructor
   */
  constructor(
    // NOSONAR
    public registerService: RegisterService,
    private authenticatedUserService: AuthenticatedUserService,
    private userService: UserService,
    public dialog: MatDialog,
    private translationService: TranslationService,
    private snackbarService: SnackBarService,
    private productService: ProductService,
    private readonly navigationService: NavigationService,
    private readonly dialogService: DialogService,
    private readonly topHeaderService: TopHeaderService
  ) {}

  /**
   * on init
   */
  ngOnInit() {
    this.registerService.resetForms(this.isUpdateBusiness);
    this.translationService.translateObject(this.representativeItemLabels);
    this.translationService.translateObject(this.errorMessages);

    const registerEquipmentFormGroupControls =
      this.registerService.registerEquipmentFormGroup.controls;
    registerEquipmentFormGroupControls.representativeName.setValue(
      this.authenticatedUserService.representative?.firstName || ''
    );
    registerEquipmentFormGroupControls.representativeLastName.setValue(
      this.authenticatedUserService.representative?.lastName || ''
    );

    this.manageRepresentatives = new UntypedFormGroup({
      email: new UntypedFormControl('', [
        Validators.required,
        Validators.email,
        emailPattern(),
        notInValidator(
          this.representativeList,
          (el: Representative) => el.email || ''
        ),
      ]),
    });

    this.subsink.add(
      this.manageRepresentatives.valueChanges.subscribe(() => {
        this.inviteErrorFeedback.reset();
      })
    );

    this.businessData = this.registerService.getBusinessDataFormObject();
    this.businessData.formGroup.disable();
    this.equipmentData =
      this.registerService.getBusinessEquipmentFormObjectPaged(
        this.page,
        this.pageSize
      );
    this.equipmentData.formGroup.disable();
    (
      this.equipmentData.formGroup.get('equipments') as UntypedFormArray
    )?.controls.forEach((control) => {
      control.disable();
    });

    this.subsink.add(
      this.equipmentData.formGroup.statusChanges.subscribe(() => {
        this.equipmentIsChanged = this.isEquipmentChanged();
      })
    );

    this.fetchingInitialInfo();

    this.authenticatedUserService.getBusinessId().then((businessId) => {
      this.businessId = businessId;
      this.listLoading = true;
      this.updateRepresentativesAndInvitees();
      this.listLoading = false;
      this.updateEquipmentData();
      this.updateBusinessData();
    });

    this.promoteDialogData = {
      content: 'confirm-promote-user-to-admin',
      successButtonText: 'confirm',
      failButtonText: 'cancel',
    };
    this.deleteRepresentativeDialogData = {
      ...this.promoteDialogData,
      content: 'confirm-delete-user',
    };
    this.deleteInviteDialogData = {
      ...this.promoteDialogData,
      content: 'confirm-delete-invite',
    };
    this.resendInviteDialogData = {
      ...this.promoteDialogData,
      content: 'confirm-resend-invite',
    };

    this.translationService.translateObject(this.promoteDialogData);
    this.translationService.translateObject(
      this.deleteRepresentativeDialogData
    );
    this.translationService.translateObject(this.deleteInviteDialogData);
    this.translationService.translateObject(this.resendInviteDialogData);
    this.translationService.translateObject(this.labels);

    this.userService
      .getTermsOfUseAndPrivacyConditionsURLs()
      .subscribe((urls) => {
        this.privacyLink = urls.privacyConditionsURL;
      });
  }

  /**
   * Cleans up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
    this.registerService.resetForms();
  }

  /**
   * Make changes to business data information permanent
   */
  async saveData() {
    this.saveDataFeedback.reset();
    this.businessDataLoading = true;
    const businessContactEmail = this.registerService
      .getBusinessDataFormObject()
      .formGroup.get('businessContactEmail')?.value;
    const checkEmail = await firstValueFrom(
      this.userService.validateBusinessEmailAuth(businessContactEmail)
    );
    if (checkEmail === 'true') {
      try {
        await this.registerService.updateBusinessData();
        this.saveDataFeedback.setSuccess();
        this.updateBusinessData();
      } catch (e) {
        this.saveDataFeedback.setError();
      } finally {
        this.businessDataLoading = false;
      }
    } else {
      this.saveDataFeedback.setExistingBusinessEmailError();
      this.businessDataLoading = false;
    }
  }

  /**
   * @return {boolean}
   */
  isSaveDataDisabled(): boolean {
    const businessDataIsNotChanged = !this.isBusinessDataChanged();

    return (
      businessDataIsNotChanged ||
      this.businessDataLoading ||
      this.businessData.formGroup.invalid
    );
  }

  /**
   * Continue the equipment register process
   */
  async nextPhase() {
    this.equipmentActionLoading = false;
    this.businessEquipmentFormPhase =
      this.registerService.businessEquipmentFormPhase;
    this.registerService.disableCountryField = false;
  }

  /**
   * Make changes to business equipment information permanent
   */
  async saveEquipment() {
    this.equipmentActionLoading = true;
    const equipmentsToAdd = [this.newEquipmentFormGroup!.getRawValue()];
    const dto = new AddRemoveEquipmentRequestDTO(equipmentsToAdd, []);

    let response = {
      added: [],
      deleted: [],
      newEquipment: false,
    };

    this.productService
      .updateEquipmentList(dto)
      .pipe(take(1))
      .subscribe({
        next: (e) => {
          response = e;
          this.equipmentActionLoading = false;
        },
        error: async (err) => {
          this.updateEquipmentData();
          this.snackbarService.error(this.labels.somethingWrong);
        },
        complete: () => {
          if (response.newEquipment) {
            this.dialogService.openDialog(
              {
                title: this.labels.title500Points,
                content: this.labels.content500Points,
                successButtonText: this.labels.ok,
              },
              undefined,
              undefined,
              450
            );
          }
          this.newEquipmentFormGroup!.enable();
          this.newEquipmentFormGroup?.get('serial')?.setValue('');
          this.newEquipmentFormGroup?.markAsUntouched();
          this.translationService
            .translateString('success-updating-data')
            .then((string) => this.snackbarService.success(string));
          this.updateEquipmentData();
          this.topHeaderService.fetchTopHeaderInfo();
        },
      });
  }

  /**
   * Deletes equipment on delete button click
   * @param {FormGroup} control
   */
  deleteEquipment(control: FormGroup) {
    const dto = new AddRemoveEquipmentRequestDTO(
      [],
      [control.get('id')?.value]
    );

    this.equipmentDataLoading = true;

    this.productService
      .updateEquipmentList(dto)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.equipmentDataLoading = false;
          this.equipmentActionLoading = false;
        },
        error: async () => {
          this.snackbarService.error(this.labels.somethingWrong);
        },
        complete: () => {
          this.translationService
            .translateString('success-updating-data')
            .then((string) => this.snackbarService.success(string));
          this.updateEquipmentData();
          this.topHeaderService.fetchTopHeaderInfo();
        },
      });
  }

  /**
   * @return {boolean}
   */
  isSaveEquipmentDisabled(): boolean {
    if (this.businessEquipmentFormPhase === 'EPSON_UNREGISTERED_PENDING') {
      return this.epsonUnregisteredSaveEquipmentDisabled();
    } else {
      return this.lnrRegisterSaveEquipmentDisabled();
    }
  }

  /**
   * Whether save equipment button is disabled when in epson unregistered phase
   * @return {boolean}
   */
  epsonUnregisteredSaveEquipmentDisabled(): boolean {
    return (
      !this.registerService.registerEquipmentFormGroup.get('placeOfPurchase')
        ?.valid ||
      !this.registerService.registerEquipmentFormGroup.get('installationDate')
        ?.valid ||
      !this.registerService.registerEquipmentFormGroup.get('agree')?.valid
    );
  }

  /**
   * Whether save equipment is disabled in lnr register phase
   * @return {boolean}
   */
  lnrRegisterSaveEquipmentDisabled(): boolean {
    return (
      this.equipmentDataLoading ||
      this.newEquipmentFormGroup!.invalid ||
      (this.isAddingEquipment &&
        (!this.equipmentIsChanged || !this.isEquipmentChanged())) ||
      this.equipmentActionLoading
    );
  }

  /**
   * @return {boolean}
   */
  isInviteRepresentativeButtonDisabled(): boolean {
    return (
      this.inviteLoading ||
      this.listLoading ||
      this.manageRepresentatives.invalid ||
      !this.authenticatedUserService.isAdmin()
    );
  }

  /**
   * @return {boolean}
   */
  isInviteRepresentativeInputDisabled(): boolean {
    return !this.authenticatedUserService.isAdmin();
  }

  /**
   * Open confirm deletion modal
   * @param {GeneralizedRepresentative} generalizedRepresentative
   */
  async openConfirmDeleteDialog(
    generalizedRepresentative: GeneralizedRepresentative
  ) {
    if (isRepresentative(generalizedRepresentative)) {
      await this.openConfirmDeleteRepresentativeDialog(
        generalizedRepresentative
      );
    } else {
      await this.openConfirmDeleteInviteDialog(generalizedRepresentative);
    }
  }

  /**
   * Opens a dialog that will remove a representative
   * @param {Representative} representative
   */
  async openConfirmDeleteRepresentativeDialog(representative: Representative) {
    if (representative?.id == null) {
      throw new Error(this.errorMessages.repIdMustBePresent);
    }
    const name = `${representative.firstName} ${representative.lastName}`;
    const dialogResponse = await this.openDialog(
      BusinessRepresentativeInteraction.DELETE_REPRESENTATIVE
    )(name);
    if (dialogResponse === MatDialogResponse.SUCCESS) {
      this.listLoading = true;
      await this.confirmDeleteRepresentative(representative.id);
      await this.updateRepresentativesAndInvitees();
      this.listLoading = false;
    }
  }

  /**
   * Opens a dialog that will remove an invitation
   * @param {InviteRepresentativeResponseDTO} invitee
   */
  async openConfirmDeleteInviteDialog(
    invitee: InviteRepresentativeResponseDTO
  ) {
    const dialogResponse = await this.openDialog(
      BusinessRepresentativeInteraction.DELETE_INVITE
    )(invitee.email);
    if (dialogResponse === MatDialogResponse.SUCCESS) {
      this.listLoading = true;
      await this.confirmDeleteInvite(invitee.id);
      await this.updateRepresentativesAndInvitees();
      this.listLoading = false;
    }
  }

  /**
   * Confirm delete action
   * @param {number} representativeId
   */
  async confirmDeleteRepresentative(representativeId: number) {
    await lastValueFrom(
      this.userService.deleteRepresentative(representativeId)
    );
  }

  /**
   * Confirm delete invite action
   * @param {number} inviteId
   */
  async confirmDeleteInvite(inviteId: number) {
    await lastValueFrom(this.userService.deleteInvite(inviteId));
  }

  /**
   * Open confirm promotion modal
   * @param {Representative} representative
   */
  async openConfirmPromotionDialog(representative: Representative) {
    if (representative.id == null) {
      throw new Error(this.errorMessages.repIdMustBePresent);
    }
    const name = `${representative.firstName} ${representative.lastName}`;
    const dialogResponse = await this.openDialog(
      BusinessRepresentativeInteraction.PROMOTE
    )(name);
    if (dialogResponse === MatDialogResponse.SUCCESS) {
      this.listLoading = true;
      await this.confirmPromotion(representative.id);
      await this.updateRepresentativesAndInvitees();
      this.listLoading = false;
    }
  }

  /**
   * Opens confirm resend invite dialog
   * @param {InviteRepresentativeResponseDTO} invitee
   */
  async openConfirmResendInviteDialog(
    invitee: InviteRepresentativeResponseDTO
  ) {
    const dialogResponse = await this.openDialog(
      BusinessRepresentativeInteraction.RESEND_INVITE
    )(invitee.email);
    if (dialogResponse === MatDialogResponse.SUCCESS) {
      this.listLoading = true;
      await this.inviteBusinessRepresentative(invitee.email);
      await this.updateRepresentativesAndInvitees();
      this.listLoading = false;
    }
  }

  /**
   * @param {BusinessRepresentativeInteraction} interaction
   * @return {function(string): Promise<MatDialogResponse>}
   */
  openDialog(
    interaction: BusinessRepresentativeInteraction
  ): (username: string) => Promise<MatDialogResponse> {
    return (username: string) => {
      let dialogData: any;
      if (interaction === BusinessRepresentativeInteraction.PROMOTE) {
        dialogData = this.promoteDialogData;
      } else if (
        interaction === BusinessRepresentativeInteraction.DELETE_REPRESENTATIVE
      ) {
        dialogData = this.deleteRepresentativeDialogData;
      } else if (
        interaction === BusinessRepresentativeInteraction.DELETE_INVITE
      ) {
        dialogData = this.deleteInviteDialogData;
      } else if (
        interaction === BusinessRepresentativeInteraction.RESEND_INVITE
      ) {
        dialogData = this.resendInviteDialogData;
      }

      const dialogRef = this.dialog.open(DialogModalComponent, {
        data: {
          ...dialogData,
          content: dialogData.content.replace('[user]', username),
        },
        autoFocus: false,
      });
      return firstValueFrom<MatDialogResponse>(dialogRef.afterClosed());
    };
  }

  /**
   * Confirm promotion action
   * @param {number} representativeId
   */
  async confirmPromotion(representativeId: number) {
    await lastValueFrom(
      this.userService.promoteRepresentative(representativeId)
    );
    this.authenticatedUserService.resetRepresentative();
    this.businessData.formGroup.disable();
  }

  /**
   * Send an invitation for a possible business representative
   * @param {string} emailToInvite
   */
  async inviteBusinessRepresentative(emailToInvite?: string) {
    let tookFromForm = false;
    let email: string;
    if (emailToInvite && emailToInvite.length > 0) {
      email = emailToInvite;
    } else if (this.manageRepresentatives.invalid) {
      throw new Error(this.errorMessages.invalidForm);
    } else {
      email = this.manageRepresentatives.get('email')!.value;
      tookFromForm = true;
    }
    this.inviteLoading = true;
    const inviteRepresentativeRequestDTO = new InviteRepresentativeRequestDTO(
      email
    );
    try {
      this.listLoading = true;
      await lastValueFrom(
        this.userService.inviteRepresentative(inviteRepresentativeRequestDTO)
      );
      await this.updateRepresentativesAndInvitees();
      this.listLoading = false;
      if (tookFromForm) {
        const emailControl = this.manageRepresentatives.get('email')!;
        emailControl.reset('');
      }
    } catch (error) {
      this.handleInviteError(error as Error);
    } finally {
      this.listLoading = false;
      this.inviteLoading = false;
    }
  }

  /**
   * Populates representative list from backend
   */
  async pullRepresentativeList() {
    const representativeList = await lastValueFrom(
      this.userService.listRepresentatives()
    );
    const activeRepresentativeList = representativeList.filter(
      (r) => r.active === true
    );
    this.representativeList.splice(0, this.representativeList.length);
    this.representativeList.push(...activeRepresentativeList);
    this.representativeList.sort((a, b) => {
      // Sorts representative list by role
      if (a.role === RepresentativeRole.BUSINESS_ADMIN) return -1;
      if (b.role === RepresentativeRole.BUSINESS_ADMIN) return 1;
      return 0;
    });
    const adminRep = this.representativeList.find(
      (rep) => rep.role === 'BUSINESS_ADMIN'
    );
    if (adminRep) {
      this.registerService.registerEquipmentFormGroup.controls.representativeName.setValue(
        adminRep.firstName
      );
      this.registerService.registerEquipmentFormGroup.controls.representativeLastName.setValue(
        adminRep.lastName
      );
    }
  }

  /**
   * Handles possible errors in inviting a representative
   * @param {Error} error
   */
  private handleInviteError(error: Error) {
    let errorMsg = 'backend-comm-error';
    if (error instanceof MessageRejectedException) {
      errorMsg = 'invite-exception-message-rejected';
    } else if (error instanceof RepresentativeLimitReachedException) {
      errorMsg = 'invite-exception-rlr-001';
    }
    this.inviteErrorFeedback.setError(errorMsg);
  }

  /**
   * Populates invitees list from backend
   */
  async pullInviteeList() {
    const inviteeList = await lastValueFrom(this.userService.listInvitees());
    this.inviteeList.splice(0, this.inviteeList.length);
    this.inviteeList.push(...inviteeList);
  }

  /**
   * Updates both lists
   */
  async updateRepresentativesAndInvitees() {
    await this.pullInviteeList();
    await this.pullRepresentativeList();
  }

  /**
   * Pulls business data from API
   */
  async updateBusinessData() {
    this.businessData = this.registerService.getBusinessDataFormObject(
      this.businessId,
      (formGroup) => {
        this.businessDataLoading = false;
        this.originalBusinessData = formGroup.value;
        const registerEquipmentControls =
          this.registerService.registerEquipmentFormGroup.controls;
        registerEquipmentControls.businessName.setValue(
          this.originalBusinessData.businessName
        );
        registerEquipmentControls.businessEmail.setValue(
          this.originalBusinessData.businessContactEmail
        );
        registerEquipmentControls.country.setValue(
          this.originalBusinessData.country
        );
        registerEquipmentControls.stateOrRegion.setValue(
          this.originalBusinessData.state
        );
        registerEquipmentControls.primaryPhone.setValue(
          this.originalBusinessData.phoneNumber
        );
      }
    );
  }

  /**
   * Pulls equipment data from API
   */
  async updateEquipmentData() {
    this.equipmentActionLoading = true;
    this.equipmentData =
      this.registerService.getBusinessEquipmentFormObjectPaged(
        this.page,
        this.pageSize,
        this.businessId,
        (formGroup) => {
          this.equipmentDataLoading = false;
          this.equipmentActionLoading = false;
          this.isAddingEquipment = false;
          this.originalEquipmentData = formGroup.value;
          this.originalEquipmentData.equipments.forEach((equipment) => {
            delete equipment['model'];
          });
        }
      );
  }

  /**
   * Pulls equipment data from API
   */
  updatePageEquipmentData() {
    if (this.isAddingEquipment) return;
    this.page++;
    this.equipmentDataLoading = true;
    this.updateEquipmentData();
  }

  /**
   * Joins representatives and invitees into a generalized list
   * @return {GeneralizedRepresentative[]}
   */
  getGeneralizedRepresentativeList(): GeneralizedRepresentative[] {
    const generalizedRepresentativeList: GeneralizedRepresentative[] = [];
    generalizedRepresentativeList.push(...this.representativeList);
    generalizedRepresentativeList.push(...this.inviteeList);
    return generalizedRepresentativeList;
  }

  /**
   * Whether business data and equipment data are loading or not
   * @param {boolean} loading if true or undefined it is loading
   */
  private fetchingInitialInfo(loading?: boolean) {
    if (loading == null) {
      loading = true;
    }

    this.businessDataLoading = loading;
    this.equipmentDataLoading = loading;
  }

  /**
   * Whether equipment data has been altered
   * @return {boolean}
   */
  private isEquipmentChanged(): boolean {
    const currentEquipmentData = JSON.stringify(
      this.newEquipmentFormGroup!.value
    );
    const originalEquipmentData = JSON.stringify(this.originalEquipmentData);
    return currentEquipmentData !== originalEquipmentData;
  }

  /**
   * Whether business data has been altered
   * @return {boolean}
   */
  private isBusinessDataChanged(): boolean {
    const currentBusinessData = JSON.stringify(
      this.businessData.formGroup.value
    );
    const originalBusinessData = JSON.stringify(this.originalBusinessData);
    return currentBusinessData !== originalBusinessData;
  }

  /**
   * Cancel equipment edit, going back to original state
   */
  editEquipmentCancelClick() {
    if (this.businessEquipmentFormPhase === 'EPSON_UNREGISTERED_PENDING') {
      this.businessEquipmentFormPhase = 'LNR_REGISTER';
      this.newEquipmentFormGroup!.enable();
    } else {
      if (this.isEquipmentChanged()) {
        this.equipmentActionLoading = true;
        this.updateEquipmentData();
      }
      this.businessEquipmentFormPhase = 'LNR_REGISTER';
      this.isAddingEquipment = false;
    }
  }

  /**
   * Next step for adding new equipment
   */
  editEquipmentNextClick() {
    const serial = this.newEquipmentFormGroup!.get('serial');
    if (!serial) {
      return;
    }
    if (this.shouldCallValidationAPI(this.equipmentsArray, serial)) {
      this.newEquipmentFormGroup!.disable();
      this.dialogService.openDialog(
        {
          title: this.labels.doYouWishToSaveChanges,
          failButtonText: this.labels.cancel,
          content: this.saveChangesText(),
          successButtonText: this.labels.saveChanges,
          contentKeepWhiteSpace: true,
        },
        () => {
          if (
            this.businessEquipmentFormPhase === 'EPSON_UNREGISTERED_PENDING' &&
            serial?.value
          ) {
            return this.epsonRegister(serial);
          }
          this.addNewEquipment(serial);
        },
        () => {
          this.newEquipmentFormGroup!.enable();
        }
      );
    }
    return;
  }

  /**
   * @param { FormArray } _equipmentsArray
   * @param { AbstractControl } serial
   * @return {boolean} whether or not validation api should be called
   */
  shouldCallValidationAPI(
    _equipmentsArray: FormArray,
    serial: AbstractControl
  ): boolean {
    const hasDuplicate = _equipmentsArray.controls.reduce(
      (acc, control) => acc || control.get('serial')?.value === serial.value,
      false
    );
    if (hasDuplicate) {
      serial.setErrors(errorObjectBuilder('duplicate-serial'));
      return false;
    }
    return true;
  }

  /**
   * Adds new equipment to the business
   * @param {AbstractControl} serial
   */
  addNewEquipment(serial: AbstractControl) {
    this.equipmentActionLoading = true;
    this.isAddingEquipment = true;
    const country = this.registerService.findCountryById(
      this.registerService.registerEquipmentFormGroup.controls.country!
        .value as number
    );
    this.registerService.validateSerial(
      serial,
      this.newEquipmentFormGroup!,
      country!.initials,
      false,
      this.saveEquipment.bind(this),
      this.nextPhase.bind(this)
    );
  }

  /**
   * Decides the label for rightmost button in equipment section
   */
  get editEquipmentNextButtonLabel() {
    if (this.isEquipmentsArrayOk && !this.isAddingEquipment) {
      return this.labels.editBusinessEquipment;
    } else if (
      this.businessEquipmentFormPhase === 'EPSON_UNREGISTERED_PENDING'
    ) {
      return this.labels.saveChanges;
    } else {
      return this.labels.validate;
    }
  }

  /**
   * Easy access for equipments array
   */
  get equipmentsArray() {
    return this.equipmentData.formGroup.controls[
      'equipments'
    ] as UntypedFormArray;
  }

  /**
   * If all equipments are validated and registered
   */
  get isEquipmentsArrayOk() {
    return this.equipmentsArray.controls.reduce(
      (acc, control) =>
        acc && (control.get('id')?.value !== undefined || control.disabled),
      true
    );
  }

  /**
   * If equipment section is loading something, either its data or its action
   */
  get isEquipmentLoading() {
    return this.equipmentDataLoading;
  }

  /**
   * Triggers epson register api
   * @param {AbstractControl} serial
   */
  epsonRegister(serial: AbstractControl) {
    this.registerService.registerEquipmentFormGroup.disable();
    this.equipmentActionLoading = true;
    const country = this.registerService.findCountryById(
      this.registerService.registerEquipmentFormGroup.controls.country!
        .value as number
    );
    this.productService
      .epsonSAPEquipmentRegistration(
        fromTRegisterEquipmentFormGroup(
          this.registerService.registerEquipmentFormGroup,
          serial.value,
          country!.initials
        )
      )
      .pipe(take(1))
      .subscribe({
        next: (sapResponse) => {
          if (sapResponse.active) {
            this.businessEquipmentFormPhase = 'LNR_REGISTER';
            this.ephemeralFeedback.feedback = 'registered';
            this.saveEquipment();
          } else {
            this.dialogService.openDialog({
              title: this.labels.couldNotRegisterPrinter,
              content: this.labels.couldNotRegisterPrinter,
              successButtonText: this.labels.ok,
            });
          }
        },
        error: () => {
          this.dialogService.openDialog({
            title: this.labels.couldNotRegisterPrinter,
            content: this.labels.noCommPrinterDatabase,
            successButtonText: this.labels.ok,
          });
        },
      })
      .add(() => {
        this.equipmentActionLoading = false;
        this.registerService.registerEquipmentFormGroup.enable();
      });
  }

  /**
   * Will display at most two lists in human readable way.
   * One lists equipments that will be added to this account,
   * and the other is of equipments that will be removed from this account.
   * @return {string}
   */
  saveChangesText() {
    const equipmentsToAdd = [this.newEquipmentFormGroup!.getRawValue()];

    const equipmentsBeingAdded =
      equipmentsToAdd.length > 0
        ? `${this.labels.equipmentsBeingADDED}:\n`
        : '';

    return `${equipmentsBeingAdded}${equipmentsToAdd
      .map((equipment: any) => '  - ' + equipment.serial)
      .join('\n')}`;
  }

  /**
   * Whether there is pending equipment to be inserted
   */
  get pendingProductInsertion() {
    return (
      this.businessEquipmentFormPhase === 'EPSON_UNREGISTERED_PENDING' ||
      !this.newEquipmentFormGroup?.valid
    );
  }

  /**
   * Whether there is pending equipment to be registered
   */
  get pendingProductRegistration() {
    return this.businessEquipmentFormPhase === 'EPSON_UNREGISTERED_PENDING';
  }

  /**
   * New Serial Form getter
   * @return {FormControl}
   */
  get serial() {
    return this.newEquipmentFormGroup?.get('serial') as FormControl;
  }
}
